import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Save,
  Play,
  Download,
  Keyboard,
  Loader2,
  FolderOpen,
  FileDown,
  Share2,
  Eye,
  AlertTriangle,
  Unlock,
} from 'lucide-react';
import { useStudioStore } from '../stores/studioStore';
import { useCanvasStore } from '../stores/canvasStore';
import { useAuthStore } from '../stores/authStore';
import ShareProjectModal from '../components/projects/ShareProjectModal';
import LockedProjectModal from '../components/projects/LockedProjectModal';
import { InlineEditableTitle } from '../components/editor/InlineEditableTitle';
import { useMappingStore } from '../stores/mappingStore';
import { usePreviewStore } from '../stores/previewStore';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import EditorToolbar from '../components/editor/EditorToolbar';
import CanvasWorkspace from '../components/editor/CanvasWorkspace';
import LayerPanel from '../components/editor/LayerPanel';
import PropertyPanel from '../components/editor/PropertyPanel';
import AlignmentToolbar from '../components/editor/AlignmentToolbar';
import KeyboardShortcutsPanel from '../components/editor/KeyboardShortcutsPanel';
import ZoomControls from '../components/editor/ZoomControls';
import { ArtboardSettingsPopover } from '../components/editor/ArtboardSettingsPopover';
import PreviewShell from '../components/editor/preview/PreviewShell';
import { ExportDialog } from '../components/editor/export/ExportDialog';
import AssetLibraryPanel from '../components/editor/AssetLibraryPanel';
import { buildElementFromAsset } from '../lib/assetInsert';
import { useAssetStore } from '../stores/assetStore';
import SaveTemplateModal from '../components/editor/SaveTemplateModal';
import ToolStrip from '../components/editor/ToolStrip';
import SaveStatusIndicator from '../components/editor/SaveStatusIndicator';
import EditAsBanner from '../components/editor/EditAsBanner';
import EditAsSelector from '../components/editor/EditAsSelector';

export default function EditorPage() {
  const {
    activeProject,
    renameProject,
    setActiveProject,
    canvasState,
    selectedElementIds,
    getSelectedElements,
    updateElements,
    groupSelected,
    ungroupSelected,
    saveProjectNow,
    saveStatus,
    flushAndCleanup,
    acquireProjectLock,
    releaseProjectLock,
    forceReleaseProjectLock,
    lockState,
    thumbnailLastError,
  } = useStudioStore();
  const { canvasWidth, canvasHeight, showLayerPanel, showPropertyPanel } = useCanvasStore();
  const { loadSchema, schema } = useMappingStore();
  const { isPreviewActive, enterPreview, exitPreview } = usePreviewStore();
  const canEditProject = useAuthStore((s) => s.canEditProject);
  const isOwnerOf = useAuthStore((s) => s.isOwnerOf);
  const isPlatformOwner = useAuthStore((s) => s.isPlatformOwner);
  const canEdit = canEditProject(activeProject);
  const isOwner = isOwnerOf(activeProject);
  const platformOwner = isPlatformOwner();
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const toggleShortcutsPanel = useCallback(() => {
    setShowShortcuts((prev) => !prev);
  }, []);

  const togglePreview = useCallback(() => {
    if (isPreviewActive) {
      exitPreview();
    } else {
      enterPreview();
    }
  }, [isPreviewActive, enterPreview, exitPreview]);

  const toggleExportDialog = useCallback(() => {
    setShowExportDialog(true);
  }, []);

  useKeyboardShortcuts({ toggleShortcutsPanel, togglePreview, toggleExportDialog });

  useEffect(() => {
    if (!schema) {
      loadSchema();
    }
  }, [schema, loadSchema]);

  useEffect(() => {
    const handleOpenModal = () => setShowSaveTemplateModal(true);
    window.addEventListener('cs:open-save-template-modal', handleOpenModal);
    return () => window.removeEventListener('cs:open-save-template-modal', handleOpenModal);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const { _autoSaveTimer } = useStudioStore.getState();
      if (_autoSaveTimer) {
        useStudioStore.getState()._executeAutoSave();
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    const handlePageHide = () => {
      const pid = useStudioStore.getState().activeProject?.id;
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const token = (useAuthStore.getState().session as { access_token?: string } | null)?.access_token;
      if (!pid || !url || !key || !token) return;
      try {
        const body = JSON.stringify({ p_project_id: pid });
        const endpoint = `${url}/rest/v1/rpc/cs_release_project_lock_safe`;
        const blob = new Blob([body], { type: 'application/json' });
        const beacon = (navigator as Navigator & {
          sendBeacon?: (url: string, data?: Blob) => boolean;
        }).sendBeacon;
        if (beacon) {
          const beaconUrl = `${endpoint}?apikey=${encodeURIComponent(key)}`;
          beacon.call(navigator, beaconUrl, blob);
        } else {
          fetch(endpoint, {
            method: 'POST',
            headers: {
              apikey: key,
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body,
            keepalive: true,
          }).catch(() => {});
        }
      } catch {
        // best-effort; stale-lock reaper handles the worst case
      }
    };
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  // Acquire the project lock on mount and release on unmount. Only attempt
  // when the user is actually allowed to edit; viewers skip the lock.
  useEffect(() => {
    if (!activeProject?.id) return;
    if (!canEdit) return;
    acquireProjectLock(activeProject.id);
    return () => {
      releaseProjectLock();
    };
  }, [activeProject?.id, canEdit, acquireProjectLock, releaseProjectLock]);

  if (!activeProject) return null;

  const handleSave = () => {
    saveProjectNow();
  };

  const handleReleaseLock = async () => {
    await releaseProjectLock();
    flushAndCleanup();
    setActiveProject(null);
  };

  const handleBack = () => {
    flushAndCleanup();
    setActiveProject(null);
  };

  const selectedElements = getSelectedElements();
  const isGroupSelected =
    selectedElementIds.length === 1 &&
    canvasState.elements.find((el) => el.id === selectedElementIds[0])?.type === 'group';

  const statusColor =
    activeProject.status === 'draft'
      ? 'bg-zinc-500/20 text-zinc-400'
      : activeProject.status === 'in_progress'
        ? 'bg-amber-500/20 text-amber-400'
        : activeProject.status === 'review'
          ? 'bg-blue-500/20 text-blue-400'
          : activeProject.status === 'approved'
            ? 'bg-emerald-500/20 text-emerald-400'
            : activeProject.status === 'published'
              ? 'bg-cyan-500/20 text-cyan-400'
              : 'bg-zinc-500/20 text-zinc-400';

  return (
    <div className="flex flex-col h-full relative">
      <div className="h-12 bg-[#111111] border-b border-white/[0.08] flex items-center px-4 gap-3 shrink-0">
        <button
          onClick={handleBack}
          className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
          title="Back to Dashboard"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="w-px h-5 bg-white/[0.08]" />

        <div className="flex items-center gap-2.5 min-w-0">
          <InlineEditableTitle
            value={activeProject.name}
            onSave={async (newName) => {
              await renameProject(activeProject.id, newName);
            }}
          />
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wider shrink-0 ${statusColor}`}>
            {activeProject.status?.replace('_', ' ')}
          </span>
          <span className="text-[10px] text-zinc-600 font-mono shrink-0">
            {canvasWidth} x {canvasHeight}
          </span>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-1.5">
          <SaveStatusIndicator />

          <button
            onClick={handleSave}
            disabled={saveStatus === 'saving' || !canEdit}
            title={!canEdit ? 'Read-only: you do not have edit access' : undefined}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
              !canEdit
                ? 'bg-white/[0.04] text-zinc-600 cursor-not-allowed'
                : saveStatus === 'saving'
                ? 'bg-cyan-500/10 text-cyan-400/60'
                : 'bg-cyan-500/15 text-cyan-400 hover:bg-cyan-500/25'
            }`}
          >
            {saveStatus === 'saving' ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}
            {saveStatus === 'saving' ? 'Saving...' : 'Save'}
          </button>

          {canEdit && lockState.status === 'held' && (
            <button
              onClick={handleReleaseLock}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] text-zinc-400 text-xs font-medium hover:bg-amber-500/15 hover:text-amber-300 transition-colors"
              title="Release this project so another designer can edit"
            >
              <Unlock size={14} />
              <span className="hidden sm:inline">Release</span>
            </button>
          )}

          {(isOwner || platformOwner) && (
            <button
              onClick={() => setShowShare(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] text-zinc-400 text-xs font-medium hover:bg-white/[0.08] hover:text-zinc-200 transition-colors"
              title="Share project"
            >
              <Share2 size={14} />
              <span className="hidden sm:inline">Share</span>
            </button>
          )}

          <button
            onClick={() => enterPreview()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-colors text-xs font-medium"
            title="Preview (P)"
          >
            <Play size={14} />
            Preview
          </button>

          <button
            onClick={() => setShowAssetLibrary(!showAssetLibrary)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              showAssetLibrary
                ? 'bg-cyan-500/15 text-cyan-400'
                : 'bg-white/[0.04] text-zinc-400 hover:bg-white/[0.08] hover:text-zinc-200'
            }`}
            title="Asset Library"
          >
            <FolderOpen size={14} />
            <span className="hidden sm:inline">Assets</span>
          </button>

          <button
            onClick={() => setShowSaveTemplateModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] text-zinc-400 text-xs font-medium hover:bg-white/[0.08] hover:text-zinc-200 transition-colors"
            title="Save as Template (Ctrl+Shift+S)"
          >
            <FileDown size={14} />
            <span className="hidden sm:inline">Template</span>
          </button>

          <EditAsSelector />

          <button
            onClick={() => setShowExportDialog(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] text-zinc-400 text-xs font-medium hover:bg-white/[0.08] hover:text-zinc-200 transition-colors"
            title="Export asset (E)"
          >
            <Download size={14} />
            Export
          </button>

          <button
            onClick={toggleShortcutsPanel}
            className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
            title="Keyboard Shortcuts (?)"
          >
            <Keyboard size={16} />
          </button>
        </div>
      </div>

      {!isPreviewActive && <EditorToolbar />}

      {isPreviewActive ? (
        <PreviewShell />
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden">
          <EditAsBanner />
          <div className="flex-1 flex overflow-hidden">
          {showLayerPanel && <LayerPanel />}
          <ToolStrip />
          <div className="flex-1 flex flex-col relative overflow-hidden">
            <AlignmentToolbar
              selectedElements={selectedElements}
              updateElements={updateElements}
              onGroup={groupSelected}
              onUngroup={ungroupSelected}
              isGroupSelected={!!isGroupSelected}
              artboardWidth={canvasState.dimensions.width}
              artboardHeight={canvasState.dimensions.height}
            />
            <CanvasWorkspace />
            <div className="absolute top-2 right-2 z-20">
              <ArtboardSettingsPopover />
            </div>
            <ZoomControls />
          </div>
          {showPropertyPanel && <PropertyPanel />}
          {showAssetLibrary && (
            <div className="w-72 flex-shrink-0 h-full">
              <AssetLibraryPanel
                onClose={() => setShowAssetLibrary(false)}
                onInsertAsset={async (asset) => {
                  const { addElement } = useStudioStore.getState();
                  const { canvasWidth: cw, canvasHeight: ch } = useCanvasStore.getState();
                  const element = await buildElementFromAsset(asset, {
                    canvasWidth: cw,
                    canvasHeight: ch,
                    elementsCount: useStudioStore.getState().canvasState.elements.length,
                  });
                  if (element) {
                    addElement(element);
                    useAssetStore.getState().incrementUsage(asset.id);
                  }
                }}
              />
            </div>
          )}
          </div>
        </div>
      )}

      <ExportDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
      />

      {showShortcuts && (
        <KeyboardShortcutsPanel onClose={() => setShowShortcuts(false)} />
      )}

      <SaveTemplateModal
        isOpen={showSaveTemplateModal}
        onClose={() => setShowSaveTemplateModal(false)}
      />

      {!canEdit && (
        <div className="absolute top-12 left-0 right-0 z-30 flex items-center justify-center pointer-events-none">
          <div className="mt-2 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/25 text-amber-300 text-[11px] flex items-center gap-1.5 shadow-lg pointer-events-auto">
            <Eye size={12} />
            Read-only — the owner has blocked you from this project
          </div>
        </div>
      )}

      {!canEdit && (
        <div
          className="absolute inset-0 top-12 pointer-events-auto"
          style={{ background: 'transparent', zIndex: 25 }}
          onKeyDownCapture={(e) => e.preventDefault()}
        />
      )}

      {showShare && activeProject && (
        <ShareProjectModal
          projectId={activeProject.id}
          ownerId={activeProject.created_by}
          onClose={() => setShowShare(false)}
        />
      )}

      {lockState.status === 'blocked' && (
        <LockedProjectModal
          heldByName={lockState.heldByName}
          heldAt={lockState.heldAt}
          onBack={handleBack}
          onForceRelease={
            platformOwner && activeProject
              ? async () => {
                  const ok = await forceReleaseProjectLock(activeProject.id);
                  if (ok) {
                    await acquireProjectLock(activeProject.id);
                  }
                }
              : undefined
          }
        />
      )}

      {platformOwner && thumbnailLastError && (
        <div className="absolute bottom-3 right-3 z-30 max-w-sm px-3 py-2 rounded-lg bg-red-500/15 border border-red-500/25 text-red-200 text-[11px] flex items-start gap-2 shadow-lg">
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">Thumbnail failed</div>
            <div className="opacity-80">{thumbnailLastError}</div>
          </div>
        </div>
      )}
    </div>
  );
}
